import axios from 'axios'
import { Buffer } from 'buffer'
import config from '../../config/local-config'
import apiConfig from '../../config/api-config'
import nrLogger from './nrLogger'
import getCookieRegex from './getCookieRegex'

const ssrHost = apiConfig.apiURL
const ssrMethod = 'return-request'
const ssrVersion = 'v1'
const ssrEndPoint = `${ssrHost}/${ssrMethod}/${ssrVersion}`
const headers = {
  headers: {
    'X-API-Key': apiConfig.apiKey,
  },
}
const eligibleStatuses = ['Pending', 'Processing', 'Shipped', 'Order Shipped', 'Delivered', 'In Transit']
const showDefaultBadgeStatus = ['ACW Deleted', 'Cancelled', 'Out of Stock']

const getBrandList = () => {
  let brandList = []

  if (__BROWSER__ && window.__COMBINED_DATA__) {
    const { widgets } = window.__COMBINED_DATA__

    widgets.some(({ name, apiDetails }) => {
      const isRMA = name === 'RMA'
      brandList = apiDetails?.claim_30days?.brands || []

      return isRMA
    })
  }

  return brandList
}

const checkIfPastReturnWindow = (poCompletionDate, brand, isHassleFree) => {
  const dateNow = new Date()
  // eslint-disable-next-line camelcase
  if (typeof poCompletionDate === 'undefined' || !poCompletionDate) {
    return false
  }
  const dateComplete = new Date(poCompletionDate)
  const timeDifference = dateNow.getTime() - dateComplete.getTime()
  const dayDifference = timeDifference / (1000 * 3600 * 24)
  const brandList = getBrandList()

  const returnWindow = brandList.includes(brand) && isHassleFree ? 30 : 90
  const isPastReturn = dayDifference > returnWindow

  return {
    isPastReturn,
    daysWarranty: returnWindow,
  }
}

const getSteps = (reasonId, poCompletionDate, isLifetimeReplacement, isHassleFree, brand) => {
  let reasonIdName = reasonId
  const isWarrantyClaim = checkIfPastReturnWindow(poCompletionDate, brand, isHassleFree)
  if (!reasonIdName && isWarrantyClaim.isPastReturn) {
    reasonIdName = 'warrantyClaim'
  }

  let serviceUrl = `${ssrHost}/${ssrMethod}/`
  if (reasonIdName) {
    if (isHassleFree) {
      serviceUrl += `v2/steps/${reasonIdName}/widgets`
    } else {
      serviceUrl += `v1/steps/${reasonIdName}/widgets`
    }
  } else if (isLifetimeReplacement) {
    serviceUrl += 'v2/steps'
  } else {
    serviceUrl += 'v1/steps'
  }

  return axios
    .get(serviceUrl, { ...headers })
    .then(returnRes => ({
      ...returnRes.data,
      reasonId: reasonIdName,
      pastDays: isWarrantyClaim.daysWarranty,
      isPastReturn: isWarrantyClaim.isPastReturn,
    }))
}

const patchApplication = async stepsPayload => {
  const serviceUrl = `${ssrEndPoint}/application`

  const response = await axios.patch(serviceUrl, stepsPayload, { ...headers })
  const { data } = response.data

  return data
}

const putApplication = async orderInfoPayload => {
  const serviceUrl = `${ssrEndPoint}/application`

  const response = await axios.put(serviceUrl, orderInfoPayload, headers)
  const { data } = response.data

  return data
}

const putSignedUrl = async (imagePayload, type = 'images') => {
  const serviceUrl = `${ssrEndPoint}/${type}/signed/s3`

  const res = await axios.put(serviceUrl, imagePayload, headers)

  const { data } = res.data

  return data
}

const getReturnDetailsByRequestID = async returnData => {
  const {
    orderId,
    orderItemId,
    email,
    creditId,
    returnRequestId,
    rmaId,
  } = returnData
  let serviceUrl = `${ssrEndPoint}/details?orderItemId=${orderItemId}&orderId=${orderId}&email=${email.replace('@', '%40')}`
  if (creditId) {
    serviceUrl += `&creditId=${creditId}`
  }
  if (returnRequestId) {
    serviceUrl += `&returnRequestId=${returnRequestId}`
  }
  if (rmaId) {
    serviceUrl += `&rmaId=${rmaId}`
  }
  const dataReturn = await axios.get(serviceUrl).then(data => data)
  return dataReturn.data.data
}

const checkIfSignedUrlIsExpired = currentUrl => {
  const urlParamsStr = currentUrl.split('?').pop()
  const decodedParams = decodeURI(urlParamsStr).replaceAll('&', '","').replaceAll('=', '":"')
  const urlParamsObject = JSON.parse(`{"${decodedParams}"}`)
  const expireTimeStampInSecond = parseFloat(urlParamsObject?.Expires || '0')

  // Less expire time by 5 seconds to avoid requesting url that are near to expiration time
  const lessTimeInSecond = 5
  const currentUtcTimeStampInSecond = Date.now() / 1000
  return currentUtcTimeStampInSecond > (expireTimeStampInSecond - lessTimeInSecond)
}

const manageFileUpload = async ({
  url,
  base64Data,
  options,
  image,
  order,
  customer,
  sku,
}) => {
  let signUrl = url
  if (checkIfSignedUrlIsExpired(url)) {
    try {
      const currentOrder = {
        orderId: order.orderId,
        merchantOrderId: order.merchantOrderId,
        item: order.items.find(e => e.sku === sku),
      }
      const data = await putSignedUrl({ image, order: currentOrder, customer })
      signUrl = data.upload.url
    } catch (error) {
      nrLogger.error('manageFileUpload_PutSignedURLMethodFail', error)
      return error
    }
  }
  try {
    return axios.put(signUrl, base64Data, options)
  } catch (error) {
    nrLogger.error('manageFileUploadMethodFail', error)
    return error
  }
}

const postApplication = async (aStepsPayload, fileUpload, formFile) => {
  const serviceUrl = `${ssrEndPoint}/application`
  let responseData = ''
  try {
    const stepsPayload = {
      ...aStepsPayload,
    }

    const formKeys = Object.keys(formFile)
    const promises = []
    for (let x = 0; x < formKeys.length; x += 1) {
      const key = formKeys[`${x}`]
      const data = formFile[`${key}`]
      const mainKeys = Object.keys(data)
      for (let y = 0; y < mainKeys.length; y +=1) {
        const mainKey = mainKeys[`${y}`]

        if (fileUpload[`${key}`][`${mainKey}`] && formFile[`${key}`][`${mainKey}`]) {
          const mainData = formFile[`${key}`][`${mainKey}`]
          const isImage = mainData.type.split('/')
          let base64Data = mainData.content
          let headersData = {
            'Content-Type': mainData.type,
          }
          if (isImage[0].toLowerCase() === 'image') {
            const base64 = mainData.file
            base64Data = Buffer.from(base64.replace(/^data:image\/\w+;base64,/, ''), 'base64')
            headersData = {
              'Content-Type': mainData.type,
              'Content-Encoding': 'base64',
            }
          }

          promises.push(
            manageFileUpload({
              url: fileUpload[`${key}`][mainData.id].upload.url,
              base64Data,
              options: {
                headers: headersData,
              },
              image: { id: mainData.id, type: mainData.type },
              customer: stepsPayload.customer,
              order: stepsPayload.order,
              sku: key,
            }),
          )
        }
      }
    }

    const imgUploadRes = await Promise.all(promises)

    const hasError = imgUploadRes.find(ret => ret.message && ret.message.indexOf('Error'))
    if (hasError) {
      throw new Error('Error Upload!')
    }

    const forterToken = getCookieRegex('forterToken') || ''
    if (forterToken && forterToken.trim() !== '') {
      Object.assign(stepsPayload, { forterToken })
    }

    const response = await axios.post(serviceUrl, stepsPayload, headers)
    responseData = response.data.data
  } catch (error) {
    nrLogger.error('postAppliction', error)
    console.log('Failed sending request. ', error.message)
    throw error
  }
  return responseData
}

const reduceImageResolution = content => new Promise((resolve, reject) => {
  try {
    const img = document.createElement('img')
    const reader = new FileReader()
    const canvas = document.createElement('canvas')
    reader.readAsDataURL(content)
    reader.onload = function (e) {
      img.src = e.target.result
      if (img.width > 0) {
        const MAX_WIDTH = 1024
        const MAX_HEIGHT = 768
        let { width, height } = img

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width
            width = MAX_WIDTH
          }
        } else if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height
          height = MAX_HEIGHT
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        ctx.drawImage(img, 0, 0, width, height)
        const dataURL = canvas.toDataURL('image/jpg')
        let encoded = dataURL.toString().replace(/^data:(.*,)?/, '')
        if (encoded.length % 4 > 0) {
          encoded += '='.repeat(4 - (encoded.length % 4))
        }
        resolve(encoded)
      } else {
        reader.readAsDataURL(content)
      }
    }
    reader.onerror = error => reject(error)
  } catch (e) {
    reject(e)
  }
})

const getBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = () => {
    // eslint-disable-next-line security/detect-unsafe-regex
    let encoded = reader.result.toString().replace(/^data:(.*,)?/, '')
    if (encoded.length % 4 > 0) {
      encoded += '='.repeat(4 - (encoded.length % 4))
    }
    resolve(encoded)
  }
  reader.onerror = error => reject(error)
})

const scrollToElem = (elemId, isButton) => {
  const d = document
  const element = d.getElementById(elemId) === null ? 0 : d.getElementById(elemId).offsetTop
  let r = elemId ? element: 0
  if (isButton || elemId === 'error-comment') {
    r -= window.innerHeight / 2
  }
  window.scrollTo({
    left: 0,
    top: r,
    behavior: 'smooth',
  })
}

const transformReturnRequests = orderDetails => {
  if (!config.orderDetailsBreakReturnRequests) return orderDetails

  const { parts = [], return_request: returnRequestsFromDetails = {} } = orderDetails

  const items = Array.isArray(parts) ? parts : [parts]
  const returnedItems = []
  let returnRequests = returnRequestsFromDetails.return || []

  if (!Array.isArray(returnRequests)) {
    returnRequests = [returnRequests]
  }

  returnRequests.forEach(returnRequest => {
    const partDetails = items.find(part => `${part.order_item_id}` === `${returnRequest.order_item_id}`)

    if (!partDetails || showDefaultBadgeStatus.includes(partDetails.order_item_status)) return false

    const itemQty = returnRequest.qty_to_return || partDetails.qty

    returnedItems.push({
      ...partDetails,
      available_return_qty: 0,
      qty: itemQty,
      returnRequest,
    })

    return true
  })

  return {
    ...orderDetails,
    parts: items
      .filter(x => x.available_return_qty && x.available_return_qty > 0 &&
        eligibleStatuses.includes(x.order_item_status))
      .concat(items.filter(x => !eligibleStatuses.includes(x.order_item_status)))
      .concat(returnedItems),
  }
}

const setPayload = (order, application, opt) => {
  const {
    email,
    id,
    orderId,
    merchantOrderId,
  } = application

  const items = []
  const fields = []

  order.forEach(item => {
    const isLifetimeReplacement = item?.isLifetimeReplacementFLag
      ? { isLifetimeReplacement: 1 }
      : {}
    let parentSku = ''
    let parentId = 0
    if (item.parentSku) {
      parentId = parseInt(item.parentSku.order_item_id, 10)
      parentSku = item.parentSku.sku
    }

    const reasons = []
    if (item.reasons && item.reasons.length) {
      item.reasons.forEach(step => {
        // If Array (applicable for Warranty Claim)
        if (Array.isArray(step)) {
          step.forEach(stepArr => {
            reasons.push({
              parent: stepArr.parent,
              question: stepArr.title,
              id: stepArr.id,
              answer: stepArr.label,
            })
          })
        } else {
          reasons.push({
            parent: step.parent,
            question: step.title,
            id: step.id,
            answer: step.label,
          })
        }
      })
    }

    if (reasons.length) {
      fields.push({
        id: {
          value: parseInt(item.order_item_id, 10),
          type: 'order_item_id',
        },
        reasons,
        images: item.images,
        documents: item.documents,
        comment: item.comment,
      })
    }

    items.push({
      quantity: {
        ordered: parseInt(item.qty, 10),
        return: parseInt(item.returnQty, 10),
      },
      sku: item.sku,
      parent: {
        sku: parentSku,
        id: parentId,
      },
      id: {
        value: parseInt(item.order_item_id, 10),
        type: 'order_item_id',
      },
      vehicle: {
        year: item.modelYear,
        make: item.makeText,
        model: item.modelText,
        submodel: item.submodel,
        engine: item.appEngines,
      },
      ...isLifetimeReplacement,
    })
  })

  if (opt === 'PUT') {
    return {
      customer: { email, id },
      order: {
        orderId,
        merchantOrderId,
        items,
      },
    }
  }
  return {
    customer: { email, id },
    order: {
      orderId,
      merchantOrderId,
      items,
    },
    fields,
  }
}

const setUploadPayload = (order, application, node, load, sku) => {
  const {
    email,
    id,
    orderId,
    merchantOrderId,
  } = application

  const idx = order.findIndex(item => item.sku === sku)

  const item = {
    sku: order[`${idx}`].sku,
    id: {
      value: parseInt(order[`${idx}`].order_item_id, 10),
      type: 'order_item_id',
    },
  }
  return {
    customer: { email, id },
    order: {
      orderId,
      merchantOrderId,
      item,
    },
    [node]: { ...load },
  }
}

const setGTMPayload = (order, application) => {
  const {
    orderStatus,
    merchantOrderId,
  } = application

  const returns = []
  order.forEach(item => {
    let reasonLabel = ''
    item.reasons.forEach(reason => {
      if (reasonLabel === '') {
        reasonLabel = reason.label
      }
    })
    returns.push({
      sku: item.sku,
      sku_title: item.sku_title,
      part_name: item.part_name,
      price: item.list_price,
      reasons: reasonLabel,
      qty: item.returnQty,
    })
    reasonLabel = ''
  })

  return {
    order: {
      oid: merchantOrderId,
      status: orderStatus,
    },
    returns,
  }
}

const ObjUtils = {
  getSteps,
  putApplication,
  patchApplication,
  postApplication,
  getReturnDetailsByRequestID,
  putSignedUrl,
  reduceImageResolution,
  getBase64,
  scrollToElem,
  eligibleStatuses,
  transformReturnRequests,
  setPayload,
  setGTMPayload,
  setUploadPayload,
}

export default ObjUtils
